import { useState } from "react";

import { Switch, FormControlLabel } from "@mui/material";
import LayoutGrid from "@mui/material/Unstable_Grid2";

import DataGrid, {
  Column,
  ColumnChooser,
  Export,
  FilterRow,
  Grouping,
  GroupPanel,
  Paging,
  Scrolling,
  SearchPanel,
  Sorting,
} from "devextreme-react/data-grid";
import PivotGrid, { FieldChooser } from "devextreme-react/pivot-grid";
import PivotGridDataSource, { Field } from "devextreme/ui/pivot_grid/data_source";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";

import { PageProps } from "@ezhou/core";
import { MainCard } from "@ezhou/app_mantis_1_1";

import { JeExportByPostingDateModel } from "./model/jeExport";
import { TessituraTransaction } from "./model/modelData";
import { DummyTessituraTransaction } from "./model/dummyData";
import { ReviewRawTransactionsTicketingPivotFields } from "./model/metaData";

type ReviewRawTransactionsFormProps = {
  pageProps: PageProps;
  jeExport: JeExportByPostingDateModel;
};

const ReviewGridColumnPrefix = "reviewGridColumn";

const buildTessituraTransactionColumns = (pageProps: PageProps, data: TessituraTransaction) => {
  const keys: string[] = [];
  for (const key in data) {
    keys.push(key);
  }
  return keys.map((key: string, index: number) => {
    return (
      <Column
        key={index}
        caption={pageProps.appUtil.getLocaleString(ReviewGridColumnPrefix + "-" + key)}
      >
        {buildColumns(pageProps, key, (data as any)[key])}
      </Column>
    );
  });
};

const buildColumns = (pageProps: PageProps, group: string, data: any) => {
  const fileds: string[] = [];
  for (const key in data) {
    fileds.push(key);
  }
  return fileds.map((field: string, index: number) => {
    const dfName = `${group}.${field}`;
    //TODO use local and dfName for caption
    return <Column key={index} dataField={dfName} caption={field} allowFiltering={true}></Column>;
  });
};

export const ReviewRawTransactions = (props: ReviewRawTransactionsFormProps) => {
  const [pivotView, setPivotView] = useState<boolean>(false);
  const [pivotViewFields, setPivotViewFields] = useState<Array<Field>>(
    ReviewRawTransactionsTicketingPivotFields,
  );
  const dataSource = new PivotGridDataSource({
    fields: pivotViewFields,
    store: props.jeExport.transactions,
  });

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPivotView(event.target.checked);
  };

  const handleExporting = (e: any) => {
    const postingDate = props.jeExport.postingDate
      ? new Date(props.jeExport.postingDate)
      : undefined;
    const postingDateSuffix = postingDate
      ? `${postingDate.getFullYear()}-${String(postingDate.getMonth() + 1).padStart(2, "0")}-${String(postingDate.getDate()).padStart(2, "0")}`
      : "";
    const fileName = `TessituraTransactions${postingDateSuffix}.xlsx`;

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Tessitura Transactions");

    exportDataGrid({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
      selectedRowsOnly: e.selectedRowsOnly,
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(
          new Blob([buffer as any], { type: "application/octet-stream" }),
          fileName,
        );
      });
    });

    e.cancel = true;
  };

  return (
    <>
      <MainCard
        title={
          <LayoutGrid container spacing={2}>
            <LayoutGrid xs={1} md={1}></LayoutGrid>
            <LayoutGrid container xs={11} md={11} justifyContent="flex-end">
              <FormControlLabel
                style={{ width: "auto" }}
                control={
                  <div style={{ width: "60%" }}>
                    <Switch
                      checked={pivotView}
                      onChange={handleSwitchChange}
                      name="checkedB"
                      color="primary"
                    />
                  </div>
                }
                label={
                  <div style={{ width: 80 }}>
                    {pivotView
                      ? props.pageProps.appUtil.getLocaleString("gridView")
                      : props.pageProps.appUtil.getLocaleString("pivotView")}
                  </div>
                }
                labelPlacement="end"
              />
            </LayoutGrid>
          </LayoutGrid>
        }
      >
        {pivotView && (
          <PivotGrid
            id="sales"
            dataSource={dataSource}
            allowSortingBySummary={true}
            allowSorting={true}
            allowFiltering={true}
            allowExpandAll={true}
            height={600}
            showBorders={true}
          >
            <FieldChooser enabled={true} />
          </PivotGrid>
        )}
        {!pivotView && (
          <DataGrid
            dataSource={props.jeExport.transactions}
            height={600}
            showBorders={true}
            allowColumnResizing={true}
            columnResizingMode="widget"
            columnMinWidth={50}
            columnAutoWidth={true}
            onExporting={handleExporting}
          >
            {/* <Scrolling mode="infinite" /> */}
            <Paging defaultPageSize={10} defaultPageIndex={0} />
            <GroupPanel visible={true} />
            <Grouping autoExpandAll={false} />
            <Export enabled={true} allowExportSelectedData={true} />
            <SearchPanel
              visible={true}
              highlightCaseSensitive={true}
              placeholder={props.pageProps.appUtil.getLocaleString("search")}
            />
            <ColumnChooser enabled={true} />
            <Sorting mode="multiple" />
            <FilterRow visible={true} />
            {buildTessituraTransactionColumns(props.pageProps, DummyTessituraTransaction)}
          </DataGrid>
        )}
      </MainCard>
    </>
  );
};
